import { useEffect, useState } from "react";
import { CheckoutSetupFields } from "@/components/CheckoutSetupFields";
import type { ProductCatalogOption } from "@/types/ProductCatalog";
import type { AutomationStepCheckout } from "@/types/Automation";
import type { StepNodeData } from "../types";

type PageOption = {
  _id: string;
  title?: string;
  route?: string;
};

type AutomationOption = {
  _id: string;
  name?: string;
  template?: { type: string; context?: unknown } | string | boolean;
};

export function CheckoutStepFields({
  step,
  onCheckoutStepChange,
}: {
  step: AutomationStepCheckout;
  onCheckoutStepChange: StepNodeData["onCheckoutStepChange"];
}) {
  const [products, setProducts] = useState<ProductCatalogOption[]>([]);
  const [pages, setPages] = useState<PageOption[]>([]);
  const [automations, setAutomations] = useState<AutomationOption[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [isLoadingPages, setIsLoadingPages] = useState(true);
  const [isLoadingAutomations, setIsLoadingAutomations] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetch("/api/products").then((res) => res.json()),
      fetch("/api/pages").then((res) => res.json()),
      fetch("/api/automations").then((res) => res.json()),
    ])
      .then(([productsRes, pagesRes, automationsRes]) => {
        if (cancelled) return;
        setProducts(productsRes.products || []);
        setPages(pagesRes.pages || []);
        setAutomations(
          (automationsRes.automations || []).filter((automation: AutomationOption) => {
            const template = automation.template;
            return template !== true && typeof template !== "string";
          })
        );
      })
      .catch(console.error)
      .finally(() => {
        if (cancelled) return;
        setIsLoadingProducts(false);
        setIsLoadingPages(false);
        setIsLoadingAutomations(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      className="p-4 nodrag"
      onPointerDownCapture={(event) => {
        if ((event.target as HTMLElement).closest(".react-flow__handle")) return;
        event.stopPropagation();
      }}
    >
      <CheckoutSetupFields
        idPrefix={`checkout-step-${step.id}`}
        value={{
          lineItems: step.data?.lineItems,
          onCheckoutSuccess: step.data?.onCheckoutSuccess,
          checkoutSettings: step.data?.checkoutSettings,
        }}
        onChange={(next) =>
          onCheckoutStepChange({
            stepId: step.id,
            lineItems: next.lineItems,
            onCheckoutSuccess: next.onCheckoutSuccess,
            checkoutSettings: next.checkoutSettings,
          })
        }
        products={products}
        pages={pages}
        automations={automations}
        isLoadingProducts={isLoadingProducts}
        isLoadingPages={isLoadingPages}
        isLoadingAutomations={isLoadingAutomations}
        showSaveCard={false}
        collapseAdvanced
      />
    </div>
  );
}
